/**
 * The Output card, sheet 20 callouts 11–13 (mission 6 WP08 T048; FR-016,
 * FR-017, C-013; D.1, ANA.11, ANA.12, TX.4, X.10, GEN.6; style guide §7.1,
 * §11; post-plan T24; post-tasks P1, R7; DM-01M3C8MP7AV3AWFNA6R365X21T), and
 * the compact row while the assistant is docked (mission 8 WP09 T045; FR-012,
 * FR-013, NFR-007; SAT.1; 20/13, 22/13; squad U6, S23; research §1.19, §1.23).
 *
 * Exactly one primary, Download, a link to the app's download route (never an
 * API route; C-005). The full card, in §8's order: Download, Export CSV
 * (`ExportButton`, through `lib/files.exportFindings`), `TransmitControl` with
 * its reason (secondary in both gate states; absent for Path B or a run that
 * is not deliverable, where Path B shows the X.10 sentence instead), the
 * secondary `assistantOpen` rail button (`data-assistant-opener`, where focus
 * returns after the panel closes; OR-10), and "Report a bureau rejection" on
 * Path A only.
 *
 * `variant="compact"` is the row Results shows above the tabs while the panel
 * is open (22/13): Download, Transmit with its reason under it, Report, in a
 * `flex flex-wrap items-start` row, so it wraps rather than overflows the
 * 532 px column at 1280 (U6). No Export CSV and no assistant button there.
 */
import { Link } from 'react-router-dom'

import { ExportButton } from '@/components/trellis/ExportButton'
import { TransmitControl } from '@/components/trellis/TransmitControl'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import type { Run } from '@/lib/api/runs'
import { useAssistantPanel } from '@/lib/assistant-panel'
import { copyText } from '@/lib/provisional-copy'

/**
 * The report-rejection switch (post-tasks R7). WP10's modal (T053, T054) is
 * droppable; if it is dropped, WP13 sets this to `false` under the marker
 * `// mission 6 WP13: WP10 modal dropped`. When false the Report control is
 * absent, not disabled (C-013).
 */
export const REPORT_REJECTION_ENABLED = true

export interface OutputCardProps {
  run: Run
  /** `compact`: the docked row above the tabs (22/13). */
  variant?: 'card' | 'compact'
  /** Only for the test of the dropped-modal branch; Results never passes it. */
  reportRejectionEnabled?: boolean
}

export function OutputCard({ run, variant = 'card', reportRejectionEnabled = REPORT_REJECTION_ENABLED }: OutputCardProps) {
  const { openPanel } = useAssistantPanel()
  const id = encodeURIComponent(run.run_id)
  const compact = variant === 'compact'
  const download = (
    <Button asChild className={compact ? undefined : 'w-full'}>
      <Link to={`/app/runs/${id}/download`}>Download</Link>
    </Button>
  )
  const report =
    run.path === 'generate' && reportRejectionEnabled ? (
      <Button asChild variant="secondary">
        <Link to={`/app/runs/${id}/report-rejection`}>Report a bureau rejection</Link>
      </Button>
    ) : null
  const transmit =
    run.path === 'analyze' ? (
      <p className="text-caption text-navy-600">{copyText('pathBNotTransmissible')}</p>
    ) : (
      <TransmitControl run={run} />
    )

  if (compact) {
    return (
      <div data-output-compact="" className="flex flex-wrap items-start gap-3">
        {download}
        {transmit}
        {report}
      </div>
    )
  }
  return (
    <Card padding="compact">
      <CardHeader>
        <CardTitle>Output</CardTitle>
      </CardHeader>
      <div className="flex flex-col items-start gap-3">
        {download}
        <ExportButton runId={run.run_id} />
        {transmit}
        <Button variant="secondary" data-assistant-opener="" onClick={() => openPanel()}>
          {copyText('assistantOpen')}
        </Button>
        {report}
      </div>
    </Card>
  )
}
